import { getApp, getApps, initializeApp, type FirebaseOptions } from 'firebase/app';
import {
  GoogleAuthProvider,
  getAuth,
  inMemoryPersistence,
  setPersistence,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

const IDENTITY_AUTH_APP_NAME = 'facetflow-identity-auth';

export interface GoogleAuthConfig extends FirebaseOptions {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

export interface GoogleCredentialTokens {
  identityPlatformToken: string;
  googleIdToken: string;
}

export function getIdentityAuth(config: GoogleAuthConfig) {
  const app = getApps().some((candidate) => candidate.name === IDENTITY_AUTH_APP_NAME)
    ? getApp(IDENTITY_AUTH_APP_NAME)
    : initializeApp(config, IDENTITY_AUTH_APP_NAME);

  return getAuth(app);
}

/** Copied from ff-gpt/src/lib/google-auth.ts — the CRM owns the session, not Firebase. */
export async function requestGoogleCredential(config: GoogleAuthConfig): Promise<GoogleCredentialTokens> {
  const auth = getIdentityAuth(config);
  await setPersistence(auth, inMemoryPersistence);

  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  const result = await signInWithPopup(auth, provider);
  const credential = GoogleAuthProvider.credentialFromResult(result);

  if (!credential?.idToken) {
    await signOut(auth);
    throw new Error('Google did not return a verifiable identity credential.');
  }

  const tokens = {
    identityPlatformToken: await result.user.getIdToken(),
    googleIdToken: credential.idToken,
  };

  await signOut(auth);
  return tokens;
}
